import { z } from 'zod';

export const uniqJobStatusSchema = z.enum(['queued', 'processing', 'done', 'failed', 'expired']);
export type UniqJobStatus = z.infer<typeof uniqJobStatusSchema>;

export const UNIQ_ERROR_CODES = [
  'NOT_VIDEO',
  'TOO_LONG',
  'FFMPEG_FAILED',
  'TIMEOUT',
  'WORKER_CRASHED',
  'WORKER_UNAVAILABLE',
] as const;
export type UniqErrorCode = (typeof UNIQ_ERROR_CODES)[number];

export const uniqJobSchema = z.object({
  id: z.number().int(),
  status: uniqJobStatusSchema,
  copiesTotal: z.number().int(),
  copiesDone: z.number().int(),
  sourceName: z.string().nullable(),
  durationSec: z.number().int().nullable(),
  // Public URLs (/uploads/uniq/<id>/copy-N.mp4). Empty unless status === 'done'.
  outputUrls: z.array(z.string()),
  errorCode: z.string().nullable(),
  delivered: z.boolean(),
  deliveryError: z.string().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
});
export type UniqJob = z.infer<typeof uniqJobSchema>;

export const uniqPricingSchema = z.object({
  // Price of one video (all copies) in credits, from settings.cost_uniq.
  costCredits: z.number().int().nonnegative(),
  balance: z.number().int().nonnegative(),
  // Free turbo videos left today; null when turbo is off.
  turboFreeLeftToday: z.number().int().nonnegative().nullable(),
});
export type UniqPricing = z.infer<typeof uniqPricingSchema>;

export const uniqConfigSchema = z.object({
  maxBytes: z.number().int(),
  maxDurationSec: z.number().int(),
  copies: z.number().int(),
});
export type UniqConfig = z.infer<typeof uniqConfigSchema>;

export const uniqJobsListResponseSchema = z.object({
  jobs: z.array(uniqJobSchema),
  pricing: uniqPricingSchema,
  config: uniqConfigSchema,
});
export type UniqJobsListResponse = z.infer<typeof uniqJobsListResponseSchema>;

export type UniqJobResponse = { job: UniqJob };

/**
 * User-facing text for a failed job. Shared by TMA (status list) and the worker
 * (bot notification) so the two never disagree.
 */
export function uniqErrorText(code: string | null, opts: { maxDurationSec: number }): string {
  switch (code) {
    case 'TOO_LONG':
      return `Ролик длиннее ${opts.maxDurationSec} секунд`;
    case 'NOT_VIDEO':
      return 'Это не видеофайл';
    default:
      return 'Не получилось обработать, попробуй другой файл или позже';
  }
}

export const UNIQ_DONE_CAPTION =
  'Готово! 5 уникальных копий твоего ролика. Сохрани в галерею и выкладывай. Ссылки на скачивание в приложении живут 24 часа';
